import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';

/* ── Animated smoky / aurora background ── */
const AuroraBackground: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Large flowing blobs — each moves on a slow, unique path */}
    <motion.div
      className="absolute rounded-full"
      style={{
        width: 900, height: 900,
        background: 'radial-gradient(circle, rgba(127,3,161,0.12) 0%, rgba(127,3,161,0.04) 40%, transparent 70%)',
        filter: 'blur(80px)',
        top: '-20%', left: '-15%',
      }}
      animate={{
        x: [0, 120, 40, -60, 0],
        y: [0, 80, 160, 60, 0],
        scale: [1, 1.15, 0.95, 1.08, 1],
      }}
      transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
    />

    <motion.div
      className="absolute rounded-full"
      style={{
        width: 700, height: 700,
        background: 'radial-gradient(circle, rgba(168,85,247,0.1) 0%, rgba(168,85,247,0.03) 45%, transparent 70%)',
        filter: 'blur(90px)',
        top: '10%', right: '-10%',
      }}
      animate={{
        x: [0, -100, -30, 80, 0],
        y: [0, 120, 50, -40, 0],
        scale: [1, 0.9, 1.12, 0.95, 1],
      }}
      transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
    />

    <motion.div
      className="absolute rounded-full"
      style={{
        width: 600, height: 600,
        background: 'radial-gradient(circle, rgba(192,132,252,0.08) 0%, rgba(192,132,252,0.02) 50%, transparent 70%)',
        filter: 'blur(70px)',
        bottom: '-5%', left: '25%',
      }}
      animate={{
        x: [0, -80, 60, -40, 0],
        y: [0, -100, -40, 70, 0],
        scale: [1, 1.1, 0.92, 1.06, 1],
      }}
      transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
    />

    {/* Smaller accent orbs for depth */}
    <motion.div
      className="absolute rounded-full"
      style={{
        width: 400, height: 400,
        background: 'radial-gradient(circle, rgba(217,70,239,0.09) 0%, transparent 60%)',
        filter: 'blur(60px)',
        top: '40%', left: '50%',
      }}
      animate={{
        x: [0, 60, -90, 40, 0],
        y: [0, -70, 30, 90, 0],
        scale: [1, 1.2, 0.85, 1.1, 1],
      }}
      transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
    />

    <motion.div
      className="absolute rounded-full"
      style={{
        width: 300, height: 300,
        background: 'radial-gradient(circle, rgba(127,3,161,0.07) 0%, transparent 60%)',
        filter: 'blur(50px)',
        top: '60%', left: '10%',
      }}
      animate={{
        x: [0, 100, 30, -50, 0],
        y: [0, -50, 60, -30, 0],
      }}
      transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
    />

    {/* SVG wave overlay for glossy structure */}
    <svg
      className="absolute bottom-0 left-0 w-full opacity-[0.04]"
      viewBox="0 0 1440 320" preserveAspectRatio="none"
      style={{ height: '55%' }}
    >
      <motion.path
        fill="url(#wave-gradient)"
        d="M0,224L48,213.3C96,203,192,181,288,186.7C384,192,480,224,576,234.7C672,245,768,235,864,208C960,181,1056,139,1152,128C1248,117,1344,139,1392,149.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        animate={{
          d: [
            'M0,224L48,213.3C96,203,192,181,288,186.7C384,192,480,224,576,234.7C672,245,768,235,864,208C960,181,1056,139,1152,128C1248,117,1344,139,1392,149.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,170.7C960,160,1056,192,1152,202.7C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,256L48,245.3C96,235,192,213,288,202.7C384,192,480,192,576,213.3C672,235,768,277,864,272C960,267,1056,213,1152,192C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,224L48,213.3C96,203,192,181,288,186.7C384,192,480,224,576,234.7C672,245,768,235,864,208C960,181,1056,139,1152,128C1248,117,1344,139,1392,149.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <defs>
        <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#7F03A1" />
          <stop offset="50%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#7F03A1" />
        </linearGradient>
      </defs>
    </svg>

    {/* Second wave layer — offset timing for depth */}
    <svg
      className="absolute bottom-0 left-0 w-full opacity-[0.03]"
      viewBox="0 0 1440 320" preserveAspectRatio="none"
      style={{ height: '50%' }}
    >
      <motion.path
        fill="#a855f7"
        d="M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        animate={{
          d: [
            'M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,256L48,261.3C96,267,192,277,288,266.7C384,256,480,224,576,208C672,192,768,192,864,202.7C960,213,1056,235,1152,240C1248,245,1344,235,1392,229.3L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
            'M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />
    </svg>

    {/* ── Web / mesh lines ── */}
    <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
      {/* Animated horizontal web lines */}
      {[15, 28, 42, 56, 70, 84].map((y, i) => (
        <motion.line
          key={`h-${i}`}
          x1="0%" y1={`${y}%`} x2="100%" y2={`${y + (i % 2 === 0 ? 3 : -2)}%`}
          stroke="#a855f7" strokeWidth={0.5}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.6, 0] }}
          transition={{ duration: 6 + i * 1.5, repeat: Infinity, ease: 'easeInOut', delay: i * 1.2 }}
        />
      ))}
      {/* Animated vertical web lines */}
      {[12, 25, 38, 52, 65, 78, 90].map((x, i) => (
        <motion.line
          key={`v-${i}`}
          x1={`${x}%`} y1="0%" x2={`${x + (i % 2 === 0 ? 2 : -2)}%`} y2="100%"
          stroke="#a855f7" strokeWidth={0.5}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.4, 0] }}
          transition={{ duration: 7 + i * 1.2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.9 }}
        />
      ))}
      {/* Diagonal accent lines */}
      {[
        { x1: '0%', y1: '30%', x2: '60%', y2: '0%' },
        { x1: '40%', y1: '100%', x2: '100%', y2: '40%' },
        { x1: '0%', y1: '70%', x2: '45%', y2: '100%' },
        { x1: '55%', y1: '0%', x2: '100%', y2: '65%' },
      ].map((line, i) => (
        <motion.line
          key={`d-${i}`}
          {...line}
          stroke="#c084fc" strokeWidth={0.4}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.35, 0] }}
          transition={{ duration: 8 + i * 2, repeat: Infinity, ease: 'easeInOut', delay: i * 1.5 }}
        />
      ))}
      {/* Intersection dots — where web lines cross */}
      {[
        { cx: '25%', cy: '28%' }, { cx: '52%', cy: '42%' }, { cx: '78%', cy: '56%' },
        { cx: '38%', cy: '70%' }, { cx: '65%', cy: '15%' }, { cx: '12%', cy: '56%' },
        { cx: '90%', cy: '28%' }, { cx: '52%', cy: '84%' },
      ].map((dot, i) => (
        <motion.circle
          key={`dot-${i}`}
          cx={dot.cx} cy={dot.cy} r={1.5}
          fill="#a855f7"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.7, 0], scale: [0.5, 1.5, 0.5] }}
          transition={{ duration: 4 + i * 0.8, repeat: Infinity, ease: 'easeInOut', delay: i * 1.1 }}
        />
      ))}
    </svg>

    {/* ── Canvas dot grid — blended into background ── */}
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.08) 2.5px, transparent 2.5px)`,
        backgroundSize: '28px 28px',
        maskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 50%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 50%, transparent 80%)',
      }}
    />
  </div>
);

const HeroSection: React.FC = () => {
  const scrollToProducts = () => {
    document.getElementById('products-preview')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #07000d 0%, #0f0118 40%, #0a000f 100%)' }}>

      <AuroraBackground />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center pt-24 pb-20">
        <p className="text-sm font-semibold tracking-[0.15em] uppercase text-purple-400 mb-10">
          AI Consulting for Small Businesses
        </p>

        <h1 className="text-[2.75rem] sm:text-5xl lg:text-[4.25rem] xl:text-7xl font-bold tracking-[-0.03em] leading-[1.08] text-white mb-7">
          Build an AI-ready business{' '}
          <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-300 bg-clip-text text-transparent">
            that ships consistently
          </span>
        </h1>

        <p className="text-[17px] sm:text-lg lg:text-xl text-white/40 max-w-2xl mx-auto leading-relaxed mb-12 font-light">
          We help small businesses go from AI overwhelmed to AI automated, with clear use cases, proven systems, and hands-on guidance.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/consulting">
            <Button size="lg" className="rounded-full font-semibold shadow-2xl shadow-purple-600/20">
              Work With Us
            </Button>
          </Link>
          <Button variant="ghost" onClick={scrollToProducts}
            className="text-white/40 hover:text-white/70 hover:bg-transparent text-sm font-medium gap-2 group">
            See what we've built
            <svg className="w-4 h-4 transition-transform group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
